<script setup>
/**
 * 복지찬스 정기구독 결제 완료 팝업 (시연 v2.4 단계4). 결제 직후 1회 노출 (토스트 대체).
 * - hasSaju=false (complete): 내사주 미등록 → 사주 등록 유도 + 이용 안내
 * - hasSaju=true  (complete2): 내사주 등록됨 → 지금 바로 이용 + (보조) 사주 등록
 * - "7일간 보지 않기"는 1회 노출이라 제외. 버튼 동작은 emit만, 실제 이동은 부모가 결정.
 */
import { useClientType } from '@/composables/useClientType';

defineProps({
  hasSaju: { type: Boolean, default: false },
});
const emit = defineEmits(['close', 'register-saju', 'guide', 'use']);
const { isMobileUi } = useClientType();
</script>

<template>
  <div class="swc-overlay" @click.self="emit('close')">
    <div class="swc-dialog" :class="{ 'swc-dialog--mobile': isMobileUi }">
      <button type="button" class="swc-close" aria-label="닫기" @click="emit('close')"></button>

      <div class="swc-icon">
        <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
      </div>

      <span class="swc-badge">🎉 구독 완료</span>
      <h2 class="swc-title">낭만만세력의 모든 서비스를<br />이용하실 수 있어요</h2>

      <!-- 내사주 등록됨 (complete2) -->
      <template v-if="hasSaju">
        <p class="swc-desc">
          구독 서비스는 <span class="swc-nasaju">[내 사주]</span>를 기반으로 제공돼요.<br />아직
          등록 전이라면, 먼저 내 사주를 등록해주세요.
        </p>
        <div class="swc-caution">
          작명 서비스는 구독에 포함되지 않아요.<br />복지포인트를 낭만포인트로 전환 후 이용해주세요.
        </div>
        <button type="button" class="swc-primary" @click="emit('use')">
          지금 바로 이용하기 <span>→</span>
        </button>
        <button type="button" class="swc-secondary" @click="emit('register-saju')">
          내 사주 변경하러 가기
        </button>
      </template>

      <!-- 내사주 미등록 (complete) -->
      <template v-else>
        <p class="swc-desc">먼저 사주를 등록하면<br />바로 시작할 수 있어요.</p>
        <p class="swc-note">작명 서비스는 별도로 제공돼요</p>
        <button type="button" class="swc-primary" @click="emit('register-saju')">
          내 사주 등록하러 가기 <span>→</span>
        </button>
        <button type="button" class="swc-secondary" @click="emit('guide')">
          이용 안내 보러가기
        </button>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.swc-overlay {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  padding-top: calc(20px + var(--safe-area-top, 0px));
  background: rgba(31, 41, 55, 0.5);
}

.swc-dialog {
  position: relative;
  width: 100%;
  max-width: 320px;
  padding: 26px 32px 32px;
  background: #fff;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
}

/* 모바일: 좌우·하단 16px (PC는 base 32px 유지) */
.swc-dialog--mobile {
  padding: 26px 16px 16px;
}

.swc-close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 24px;
  height: 24px;
  border: none;
  background: url('@manseImages/icon_close_1.svg') no-repeat center / contain;
  cursor: pointer;
}

.swc-icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 12px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e8f0f9 0%, #d9e5f2 100%);
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 28px;
    height: 28px;
    stroke: #1e3a5f;
    stroke-width: 2.5;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
}

.swc-badge {
  display: inline-block;
  margin-bottom: 10px;
  padding: 4px 12px;
  background: #7c3aed;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.2px;
  border-radius: 100px;
}

.swc-title {
  margin-bottom: 8px;
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.4;
}

.swc-desc {
  margin-bottom: 7px;
  font-size: 14px;
  color: #374151;
  line-height: 1.6;
}

.swc-note {
  margin-bottom: 20px;
  font-size: 11.5px;
  font-weight: 500;
  color: #9ca3af;
}

.swc-nasaju {
  color: #1e3a5f;
  font-weight: 700;
}

.swc-caution {
  margin: 4px 0 18px;
  padding: 11px 13px;
  background: #f9fafb;
  border-radius: 10px;
  font-size: 13.5px;
  color: #374151;
  line-height: 1.65;
}

.swc-primary {
  width: 100%;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 14px;
  background: #1e3a5f;
  color: #fff;
  border: none;
  border-radius: 12px;
  font-family: inherit;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(30, 58, 95, 0.2);
  transition: background 0.15s;

  &:hover {
    background: #244873;
  }
}

.swc-secondary {
  width: 100%;
  padding: 13px;
  background: #fff;
  color: #1e3a5f;
  border: 1.5px solid #1e3a5f;
  border-radius: 12px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #f0f6ff;
  }
}
</style>
